import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import { DollarSign, TrendingUp, TrendingDown, Wallet, Receipt, Building2, MessageSquare, Users, Info } from "lucide-react";

const COLORS = ["#1E4631", "#D96C4A", "#E3A72F"];

const KPI = ({ label, value, sub, icon: Icon, color, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `${color}15`, color }}>
        <Icon className="w-5 h-5"/>
      </div>
    </div>
    <div className="mt-3 text-2xl font-[Outfit] font-bold">{value}</div>
    <div className="text-xs text-[#5C6B61] mt-0.5">{label}</div>
    {sub && <div className="text-[10px] text-[#5C6B61] mt-1 italic">{sub}</div>}
  </div>
);

const fmtUSD = (n) => n == null ? "—" : `$${(Math.round(n * 100) / 100).toLocaleString("fr-FR")}`;

export default function SuperFinance() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats/finance`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-[#5C6B61]">Chargement…</div>;

  const c = data.current;
  const profitable = c.net_monthly_usd >= 0;

  const scenarioChartData = data.scenarios_12_months.map(s => ({
    label: s.label, MRR: s.mrr, ARR: s.arr,
  }));
  const distData = [
    { name: "Pilote (gratuit)", value: data.distribution.pilot },
    { name: "Standard ($250)", value: data.distribution.standard },
    { name: "Premium ($500)", value: data.distribution.premium },
  ];

  return (
    <div className="space-y-6" data-testid="super-finance-page">
      <div>
        <h1 className="text-3xl font-[Outfit] font-bold inline-flex items-center gap-3">
          <DollarSign className="w-7 h-7 text-[#2E7D32]"/> Finances & Projections
        </h1>
        <p className="text-[#5C6B61] mt-1 text-sm">Tableau de bord financier projeté basé sur les données actuelles · USD</p>
      </div>

      <div className="bg-[#E3A72F]/10 border border-[#E3A72F]/30 rounded-xl px-5 py-3 text-sm flex items-start gap-3">
        <Info className="w-5 h-5 text-[#E3A72F] mt-0.5 flex-shrink-0"/>
        <div>
          <strong>Hypothèses pricing</strong> · Plan Pilote {fmtUSD(data.pricing.pilot_usd_month)}/mois (gratuit pour les 5 premiers hôpitaux) · Standard {fmtUSD(data.pricing.standard_usd_month)}/mois · Premium {fmtUSD(data.pricing.premium_usd_month)}/mois · Coût SMS estimé {fmtUSD(data.pricing.sms_cost_usd)} (Twilio RDC). Modifiez dans `server.py`.
        </div>
      </div>

      {/* Current state */}
      <div>
        <h3 className="font-[Outfit] font-semibold text-lg mb-3">État actuel</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI label="MRR (Revenus mensuels)" value={fmtUSD(c.mrr_usd)} sub={`ARR: ${fmtUSD(c.arr_usd)}`} icon={TrendingUp} color="#2E7D32" testid="kpi-mrr"/>
          <KPI label="Coûts mensuels" value={fmtUSD(c.total_monthly_cost_usd)} sub={`Infra + SMS + Équipe`} icon={TrendingDown} color="#D96C4A" testid="kpi-costs"/>
          <KPI label={profitable ? "Marge nette / mois" : "Burn rate / mois"} value={fmtUSD(c.net_monthly_usd)} sub={profitable ? "✅ Profitable" : "⚠️ Levée requise"} icon={Wallet} color={profitable ? "#2E7D32" : "#C62828"} testid="kpi-net"/>
          <KPI label="Runway" value={c.runway_months ? `${c.runway_months} mois` : "∞"} sub={c.runway_months ? "Trésorerie / burn" : "Profitable"} icon={Receipt} color="#1E4631" testid="kpi-runway"/>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6">
          <h3 className="font-[Outfit] font-semibold mb-4">Décomposition des coûts (mensuel)</h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-[#E5EBE7]">
              <tr><td className="py-2 text-[#5C6B61]">Infrastructure (hosting, Resend, GA)</td><td className="text-right font-mono">{fmtUSD(c.monthly_infra_cost_usd)}</td></tr>
              <tr><td className="py-2 text-[#5C6B61]">SMS (sur les 30 derniers jours)</td><td className="text-right font-mono">{fmtUSD(c.monthly_sms_cost_usd)}</td></tr>
              <tr><td className="py-2 text-[#5C6B61]">Équipe (estimation)</td><td className="text-right font-mono">{fmtUSD(c.monthly_team_burn_usd)}</td></tr>
              <tr className="font-bold"><td className="py-2">Total</td><td className="text-right font-mono">{fmtUSD(c.total_monthly_cost_usd)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6">
          <h3 className="font-[Outfit] font-semibold mb-4">Distribution des plans hôpitaux</h3>
          <div className="h-56">
            {distData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={distData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                    {distData.map((_, i) => <Cell key={i} fill={COLORS[i]}/>)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7", fontSize: 12 }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-full grid place-items-center text-sm text-[#5C6B61]">Aucun hôpital actif</div>}
          </div>
        </div>
      </div>

      {/* Scenarios */}
      <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6" data-testid="scenarios-chart">
        <h3 className="font-[Outfit] font-semibold mb-2">Projections à 12 mois — 3 scénarios</h3>
        <p className="text-xs text-[#5C6B61] mb-4">Estimation revenue annuelle selon le nombre d'hôpitaux acquis & taux de conversion pilote → payant.</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={scenarioChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5C6B61" }} stroke="#E5EBE7"/>
              <YAxis tick={{ fontSize: 11, fill: "#5C6B61" }} stroke="#E5EBE7" tickFormatter={(v) => `$${v}`}/>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7", fontSize: 12 }} formatter={(v) => `$${v.toLocaleString("fr-FR")}`}/>
              <Legend wrapperStyle={{ fontSize: 12 }}/>
              <Bar dataKey="MRR" fill="#1E4631" radius={[4, 4, 0, 0]}/>
              <Bar dataKey="ARR" fill="#D96C4A" radius={[4, 4, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          {data.scenarios_12_months.map((s, i) => (
            <div key={s.label} className="p-3 rounded-lg" style={{ background: `${COLORS[i]}10`, border: `1px solid ${COLORS[i]}30` }} data-testid={`scenario-${s.label.toLowerCase()}`}>
              <div className="font-semibold text-sm" style={{ color: COLORS[i] }}>{s.label}</div>
              <div className="text-xs text-[#5C6B61] mt-1">{s.hospitals_target} hôpitaux · {s.paying} payants</div>
              <div className="text-lg font-[Outfit] font-bold mt-1">{fmtUSD(s.arr)}<span className="text-xs text-[#5C6B61] font-normal"> /an</span></div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Hôpitaux actifs" value={c.hospitals_active} sub={`${c.hospitals_pending} en attente`} icon={Building2} color="#1E4631" testid="kpi-hospitals"/>
        <KPI label="Patients totaux" value={c.patients_total} sub={`Moy. ${c.avg_patients_per_hospital}/hôpital`} icon={Users} color="#D96C4A" testid="kpi-patients"/>
        <KPI label="SMS 30 derniers jours" value={c.sms_last_30d} sub={`Total: ${c.sms_total}`} icon={MessageSquare} color="#2E7D32" testid="kpi-sms"/>
        <KPI label="Coût SMS / mois" value={fmtUSD(c.monthly_sms_cost_usd)} sub="À 0,05$/SMS (estimation)" icon={Receipt} color="#E3A72F" testid="kpi-sms-cost"/>
      </div>
    </div>
  );
}
